import { useMemo, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { BiSortAlt2 } from "react-icons/bi";
import { requisitionsService } from "../../services/requisitions.services";
import { patientService } from "../../services/patient.services";
import { helpFunc } from "../../helpers/helpFunction";
import { PAGINATION_PER_PAGE_LIMIT } from "../../helpers/constants";

const applicant = [
  "applicant__name",
  "applicant__name__contains",
  "applicant__name__endswith",
  "applicant__name__startswith",
];

const birthNumber = [
  "patient__birth_number",
  "patient__birth_number__contains",
  "patient__birth_number__startswith",
  "patient__birth_number__endswith",
];

const externalId = [
  "patient__external_id",
  "patient__external_id__contains",
  "patient__external_id__startswith",
  "patient__external_id__endswith",
];

const patientName = [
  "patient__name",
  "patient__name__contains",
  "patient__name__endswith",
  "patient__name__startswith",
];

const patientAge = ["patient__age", "patient__age__max", "patient__age__min"];

const diagnosis = [
  "care__main_diagnosis__name",
  "care__main_diagnosis__name__contains",
  "care__main_diagnosis__name__endswith",
  "care__main_diagnosis__name__startswith",
];

const clinic = [
  "care__clinic__description",
  "care__clinic__description__contains",
  "care__clinic__description__endswith",
  "care__clinic__description__startswith",
];

const department = [
  "care_actual_department_description",
  "care_actual_department_description__contains",
  "care_actual_department_description__endswith",
  "care_actual_department_description__startswith",
];

const careDays = [
  "care_days_count",
  "care_days_count__max",
  "care_days_count__min",
];

const lastDekurz = [
  "care_last_dekurz_date",
  "care_last_dekurz_date__max",
  "care_last_dekurz_date__min",
];

const careStarted = [
  "care__started_at",
  "care__started_at__max",
  "care__started_at__min",
];

const doctorName = [
  "care_doctor_name",
  "care_doctor_name__contains",
  "care_doctor_name__endswith",
  "care_doctor_name__startswith",
];

const category = [
  "care__category__name",
  "care__category__name__contains",
  "care__category__name__endswith",
  "care__category__name__startswith",
];

const defaultFilter = {
  ...externalId.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...birthNumber.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...applicant.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...patientName.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...patientAge.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...diagnosis.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...clinic.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...department.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...careDays.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...lastDekurz.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...careStarted.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...doctorName.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
  ...category.reduce((a, v) => ({ ...a, [v]: undefined }), {}),
};

export const RequisitionsListLogic = ({ careType }) => {
  const [data, setData] = useState({ data: [], pagination: { count: 0 } });
  const [error, setError] = useState({ error: false });
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState({
    care__care_type: careType,
    offset: 0,
    page: 1,
    limit: PAGINATION_PER_PAGE_LIMIT,
    o: "applicant__name",
    state: "pending",
  });

  const storageFilter = JSON.parse(localStorage.getItem("requisitions-filter"));

  const [filter, setFilter] = useState(
    storageFilter ? storageFilter : defaultFilter
  );

  const history = useHistory();

  useEffect(() => {
    getRequisitions();
  }, [careType]);

  useEffect(() => {
    localStorage.setItem("requisitions-filter", JSON.stringify(filter));
  }, [filter]);

  const handleDateChange = (event, field) => {
    resetTableFilterValues(field);

    setFilter((prevFilter) => ({
      ...prevFilter,
      [field]: event,
    }));
  };

  const handleFilterChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;

    resetTableFilterValues(name);

    setFilter((prevFilter) => ({
      ...prevFilter,
      [name]: value,
    }));
  };

  const handleResetFilter = () => {
    localStorage.removeItem("requisitions-filter");
    localStorage.removeItem("requisitions-ordering");
    window.location.reload(false);
  };

  const getRequisitions = async (
    offset = params.offset,
    page = params.page,
    o = params.o
  ) => {
    setLoading(true);
    localStorage.setItem("requisitions-page", page);

    const data = { ...filter };

    const response = await requisitionsService.getRequisitions({
      ...params,
      care__care_type: careType,
      offset,
      page,
      o,
      ...filterMapper(data),
    });

    if (response.data.results.length === 0) {
      setData({
        data: [],
        pagination: { count: response.data.count },
      });
      setError({ error: true, message: "Seznam žádanek je prázdny." });
      setLoading(false);
    }

    if (response.data.results.length !== 0) {
      setData({
        data: await parseData(response.data.results),
        pagination: { count: response.data.count },
      });
      setParams({
        ...params,
        page: response.data.page,
        offset: response.data.offset,
        o,
      });
      setLoading(false);
    }
  };

  const parseData = async (data) => {
    return Promise.all(
      data.map(async (item) => {
        const patient = item.care.patient;
        let activeCare = item.care;
        activeCare = helpFunc.replaceNull(activeCare);

        return {
          ...patient,
          cares: [activeCare],
          id: patient?.id,
          external_id: patient?.external_id,
          age: helpFunc.countPatientAgeFromMounth(item.patient_age_months),
          diagnoses: activeCare?.main_diagnosis.name,
          days_in_hospital: item.care_days_count,
          last_dekurz: helpFunc.formatDate(item?.care_last_dekurz_date),
          last_dekurz_timestamp: new Date(
            activeCare?.last_dekurz?.made_at
          ).getTime(),
          doctor_name: item?.care_doctor_name,
          ambulant_doctor: item.care?.ambulant_doctor
            ? `${item.care?.ambulant_doctor.f_title} ${item.care?.ambulant_doctor.name} ${item.care?.ambulant_doctor.l_title}`
            : "Žádne informace",
          department: item.care_actual_department_description,
          category: activeCare?.category
            ? activeCare?.category.name
            : "Žádne informace",
          first_visit: helpFunc.formatDate(activeCare?.started_at),
          clinic: item.care_actual_clinic_description,
          startedAt:
            helpFunc.formatDate(item?.care?.created_at) || "Žádne informace",
        };
      })
    );
  };

  const resetTableFilterValues = (name) => {
    if (applicant.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        applicant: undefined,
        applicant__name__contains: undefined,
        applicant__name__endswith: undefined,
        applicant__name__startswith: undefined,
      }));
    }

    if (birthNumber.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        patient__birth_number: undefined,
        patient__birth_number__contains: undefined,
        patient__birth_number__startswith: undefined,
        patient__birth_number__endswith: undefined,
      }));
    }

    if (externalId.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        patient__external_id: undefined,
        patient__external_id__contains: undefined,
        patient__external_id__startswith: undefined,
        patient__external_id__endswith: undefined,
      }));
    }

    if (patientName.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        patient__name: undefined,
        patient__name__contains: undefined,
        patient__name__endswith: undefined,
        patient__name__startswith: undefined,
      }));
    }

    if (patientAge.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        patient__age: undefined,
        patient__age__max: undefined,
        patient__age__min: undefined,
      }));
    }

    if (diagnosis.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        care__main_diagnosis__name: undefined,
        care__main_diagnosis__name__contains: undefined,
        care__main_diagnosis__name__endswith: undefined,
        care__main_diagnosis__name__startswith: undefined,
      }));
    }

    if (clinic.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        care__clinic__description: undefined,
        care__clinic__description__contains: undefined,
        care__clinic__description__endswith: undefined,
        care__clinic__description__startswith: undefined,
      }));
    }

    if (department.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        care_actual_department_description: undefined,
        care_actual_department_description__contains: undefined,
        care_actual_department_description__endswith: undefined,
        care_actual_department_description__startswith: undefined,
      }));
    }

    if (careDays.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        care_days_count: undefined,
        care_days_count__max: undefined,
        care_days_count__min: undefined,
      }));
    }

    if (lastDekurz.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        care_last_dekurz_date: undefined,
        care_last_dekurz_date__max: undefined,
        care_last_dekurz_date__min: undefined,
      }));
    }

    if (careStarted.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        care__started_at: undefined,
        care__started_at__max: undefined,
        care__started_at__min: undefined,
      }));
    }

    if (doctorName.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        care_doctor_name: undefined,
        care_doctor_name__contains: undefined,
        care_doctor_name__endswith: undefined,
        care_doctor_name__startswith: undefined,
      }));
    }

    if (category.some((item) => item === name)) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        care__category__name: undefined,
        care__category__name__contains: undefined,
        care__category__name__endswith: undefined,
        care__category__name__startswith: undefined,
      }));
    }
  };

  const handleTableSort = (orderBy) => {
    getRequisitions(params.offset, params.page, getOrderingValue(orderBy));
  };

  const getOrderingValue = (value) => {
    if (value === params.o) {
      const orderingValue = value.includes("-") ? value : `-${value}`;
      localStorage.setItem("requisitions-ordering", orderingValue);
      return orderingValue;
    }
    localStorage.setItem("requisitions-ordering", value);
    return value;
  };

  const columns = [];
  columns["hospitalization"] = useMemo(() => [
    {
      Header: () => (
        <>
          <span>ID pacienta</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__patient__external_id")}
            />
          </span>
        </>
      ),
      accessor: "external_id",
    },
    {
      Header: () => (
        <>
          <span>Příjmení a jméno</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__patient__name")}
            />
          </span>
        </>
      ),
      accessor: "name",
    },
    {
      Header: () => (
        <>
          <span>RČ</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__patient__birth_number")}
            />
          </span>
        </>
      ),
      accessor: "birth_number",
    },
    {
      Header: () => (
        <>
          <span>Věk</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("patient_age")}
            />
          </span>
        </>
      ),
      accessor: "age",
    },
    {
      Header: () => (
        <>
          <span>DG</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__main_diagnosis__name")}
            />
          </span>
        </>
      ),
      accessor: "diagnoses",
    },
    {
      Header: () => (
        <>
          <span>Klinika</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__clinic__description")}
            />
          </span>
        </>
      ),
      accessor: "clinic",
    },
    {
      Header: () => (
        <>
          <span>Oddělení</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__clinic__description")}
            />
          </span>
        </>
      ),
      accessor: "department",
    },
    {
      Header: () => (
        <>
          <span>Počet dnů hospitalizace</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care_days_count")}
            />
          </span>
        </>
      ),
      accessor: "days_in_hospital",
    },
    {
      Header: () => (
        <>
          <span>Datum pos. dekurzu</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care_last_dekurz_date")}
            />
          </span>
        </>
      ),
      accessor: "last_dekurz_timestamp",
      Cell: ({ row }) => row.original.last_dekurz,
    },
    {
      Header: () => (
        <>
          <span>Jméno lékaře</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care_doctor_name")}
            />
          </span>
        </>
      ),
      accessor: "doctor_name",
      Cell: ({ row }) => row.original?.doctor_name,
    },
    {
      Header: () => null,
      id: "expander",
      Cell: ({ row }) => (
        <>
          <button
            type="button"
            className="button desktop-visible"
            onClick={() => openPatient(row)}
          >
            Otevřít pacienta
          </button>
          <i
            style={{ color: "#ffffff" }}
            className="fas fa-user-injured mobile-visible mr-2 table-icon"
            onClick={() => openPatient(row)}
          ></i>
          <i
            className="fas fa-info-circle mobile-visible ml-2 table-icon"
            {...row.getToggleRowExpandedProps()}
            style={{ color: "#ffffff" }}
          ></i>
          <button
            type="button"
            className="button desktop-visible"
            {...row.getToggleRowExpandedProps()}
          >
            Info o pacientovi
          </button>
        </>
      ),
    },
    { Header: "", accessor: "more_information" },
    { Header: "Status", accessor: "status" },
  ]);

  columns["ambulation"] = useMemo(() => [
    {
      Header: () => (
        <>
          <span>ID pacienta</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__patient__external_id")}
            />
          </span>
        </>
      ),
      accessor: "external_id",
    },
    {
      Header: () => (
        <>
          <span>Příjmení a jméno</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__patient__name")}
            />
          </span>
        </>
      ),
      accessor: "name",
    },
    {
      Header: () => (
        <>
          <span>RČ</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__patient__birth_number")}
            />
          </span>
        </>
      ),
      accessor: "birth_number",
    },
    {
      Header: () => (
        <>
          <span>Věk</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("patient_age")}
            />
          </span>
        </>
      ),
      accessor: "age",
    },
    {
      Header: () => (
        <>
          <span>DG</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__main_diagnosis__name")}
            />
          </span>
        </>
      ),
      accessor: "diagnoses",
    },
    {
      Header: () => (
        <>
          <span>Klinika</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__clinic__description")}
            />
          </span>
        </>
      ),
      accessor: "clinic",
    },
    {
      Header: () => (
        <>
          <span>Oddělení</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() =>
                handleTableSort("care_actual_department_description")
              }
            />
          </span>
        </>
      ),
      accessor: "department",
    },

    {
      Header: () => (
        <>
          <span>Datum návštěvy ambulance</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("created_at")}
            />
          </span>
        </>
      ),
      accessor: "first_visit",
    },
    {
      Header: () => (
        <>
          <span>Jméno lékaře</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care_doctor_name")}
            />
          </span>
        </>
      ),
      accessor: "doctor_name",
      Cell: ({ row }) => row.original?.ambulant_doctor,
    },
    {
      Header: () => (
        <>
          <span>Kategorie</span>
          <span>
            <BiSortAlt2
              style={{ cursor: "pointer" }}
              onClick={() => handleTableSort("care__category__name")}
            />
          </span>
        </>
      ),
      accessor: "category",
    },
    {
      Header: () => null,
      id: "expander",
      Cell: ({ row }) => (
        <>
          <button
            type="button"
            className="button desktop-visible"
            onClick={() => openPatient(row)}
          >
            Otevřít pacienta
          </button>
          <i
            style={{ color: "#ffffff" }}
            className="fas fa-user-injured mobile-visible mr-2 table-icon"
            onClick={() => openPatient(row)}
          ></i>
          <i
            className="fas fa-info-circle mobile-visible ml-2 table-icon"
            {...row.getToggleRowExpandedProps()}
            style={{ color: "#ffffff" }}
          ></i>
          <button
            type="button"
            className="button desktop-visible"
            {...row.getToggleRowExpandedProps()}
          >
            Info o pacientovi
          </button>
        </>
      ),
    },
    { Header: "", accessor: "more_information" },
    { Header: "Status", accessor: "status" },
  ]);

  const openPatient = (row) => {
    row.original.cares[0].checkin !== ""
      ? history.push(
          `/patient-profile/${row.original.id}/care/${row.original.cares[0].id}/pharmacological-plan`
        )
      : history.push(
          `/patient-entry-control/${row.original.id}/care/${row.original.cares[0].id}`
        );
  };

  const togglePopup = () => {
    setError({ error: false });
  };

  return {
    columns,
    data,
    loading,
    params,
    filter,
    error,
    togglePopup,
    getRequisitions,
    handleResetFilter,
    handleFilterChange,
    handleDateChange,
  };
};

const filterMapper = (obj) => {
  const maped = obj;
  for (const key in maped) {
    if (maped[key] === undefined || maped[key] === "" || maped[key] === null) {
      delete maped[key];
    }
  }

  if (maped.care_last_dekurz_date)
    maped.care_last_dekurz_date = helpFunc.parseDate(
      maped.care_last_dekurz_date
    );

  if (maped.care_last_dekurz_date__max)
    maped.care_last_dekurz_date__max = helpFunc.parseDate(
      maped.care_last_dekurz_date__max
    );

  if (maped.care_last_dekurz_date__min)
    maped.care_last_dekurz_date__min = helpFunc.parseDate(
      maped.care_last_dekurz_date__min
    );

  if (maped.care__started_at)
    maped.care__started_at = helpFunc.parseDate(maped.care__started_at);

  if (maped.care__started_at__max)
    maped.care__started_at__max = helpFunc.parseDate(
      maped.care__started_at__max
    );

  if (maped.care__started_at__min)
    maped.care__started_at__min = helpFunc.parseDate(
      maped.care__started_at__min
    );
  return maped;
};
